import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Row, Col, Spin, message, Tabs, Card, Button, Modal, Empty, Popconfirm } from "antd";
import TopHeaderBlock from "./components/TopHeaderBlock";
import LeftInformationPanel from "./components/LeftInformationPanel";
import WorkHistoryPanel from "./components/WorkHistoryPanel";
import OrdersPanel from "./components/OrdersPanel";
import Layout from '../../Layout'
import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { getCustomerDetail, updateCustomer, uploadChannelCustomerAvatar, markSpamCustomer, CustomerDetailData } from "../../api/ChannelCustomer";
import { mixGetV1 } from "../../services/mixin";
import useAuthStore from "../../store/authStore";
import StatsCard from "./components/StatsCard";
import CustomerProfile from "./components/CustomerProfile";
import DemoDashboard from "./components/DemoDashboard";
import HistoryList from "./components/HistoryList";
import { ImageIcon, ShieldAlert } from "lucide-react";
import { Image } from "antd";

export default function CustomerDetails() {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const { isSuperAdmin } = useAuthStore();
    // Phân công (trực page / telesale) chỉ Super Admin được đổi
    const canEditAssignment = isSuperAdmin;
    const [loading, setLoading] = useState(true);
    const [customer, setCustomer] = useState<CustomerDetailData | null>(null);
    const [staffOptions, setStaffOptions] = useState<any[]>([]);
    const [telesaleOptions, setTelesaleOptions] = useState<any[]>([]);
    const [customerTags, setCustomerTags] = useState<any[]>([]);
    const [careStatuses, setCareStatuses] = useState<any[]>([]);
    const [occupations, setOccupations] = useState<any[]>([]);
    const [activeTab, setActiveTab] = useState("1");

    const [openSpamModal, setOpenSpamModal] = useState(false);
    const [unmarkingSpam, setUnmarkingSpam] = useState(false);

    const fetchCustomer = useCallback(async (showLoading = true) => {
        if (!id) return;
        if (showLoading) setLoading(true);
        const res = await getCustomerDetail(Number(id));
        if (res.status && res.data) {
            setCustomer(res.data);
        }
        if (showLoading) setLoading(false);
    }, [id]);

    const fetchOptions = useCallback(async () => {
        try {
            const [staffRes, telesaleRes, tagRes, careRes, occRes] = await Promise.all([
                mixGetV1('/api/v1/be/staffs', { type_user: 1, limit: 200, type_department_id: 2 }).catch(() => null),
                mixGetV1('/api/v1/be/staffs', { type_user: 1, limit: 200, type_department_id: 3 }).catch(() => null),
                mixGetV1('/api/v1/be/customer-tags', { limit: 100, sort: ["id,desc"], group: 'group_1' }).catch(() => null),
                mixGetV1('/api/v1/be/customer-care-statuses', { take: 100 }).catch(() => null),
                mixGetV1('/api/v1/be/customer-occupations', { take: 100 }).catch(() => null),
            ]);
            if (staffRes?.status && staffRes.data?.items) {
                setStaffOptions(staffRes.data.items.map((s: any) => ({ label: s.name, value: s.id })));
            }
            if (telesaleRes?.status && telesaleRes.data?.items) {
                setTelesaleOptions(telesaleRes.data.items.map((s: any) => ({ label: s.name, value: s.id })));
            }
            if (tagRes?.status && tagRes.data?.items) {
                setCustomerTags(tagRes.data.items.map((t: any) => ({ label: t.name, value: t.id, color: t.color })));
            }
            if (careRes?.status && careRes.data?.items) {
                setCareStatuses(careRes.data.items.map((s: any) => ({ label: s.name, value: s.id, color: s.color })));
            }
            if (occRes?.status && occRes.data?.items) {
                setOccupations(occRes.data.items.map((o: any) => ({ label: o.name, value: o.id })));
            }
        } catch (e) {
            console.error("Failed to fetch options:", e);
        }
    }, []);

    useEffect(() => {
        fetchCustomer();
        fetchOptions();
    }, [fetchCustomer, fetchOptions]);

    const handleUpdateField = async (field: string, value: any) => {
        if (!customer) return;
        if ((field === 'staff_id' || field === 'telesale_id') && !canEditAssignment) {
            message.warning('Chỉ Super Admin mới được thay đổi phân công');
            return;
        }
        const res = await updateCustomer(customer.id, { display_name: customer.display_name, [field]: value });
        if (res.status) {
            setCustomer(prev => prev ? { ...prev, [field]: value } : prev);
            message.success(t("roomchat.update_success") || "Cập nhật thành công");
        } else {
            message.error(t("roomchat.update_failed") || "Cập nhật thất bại");
        }
    };

    const handleUpdateAddress = async (data: { province_code: string; ward_code: string; address: string }) => {
        if (!customer) return;
        const res = await updateCustomer(customer.id, { display_name: customer.display_name, ...data });
        if (res.status) {
            setCustomer(prev => prev ? { ...prev, ...data } : prev);
            message.success(t("roomchat.update_success") || "Cập nhật thành công");
        } else {
            message.error(t("roomchat.update_failed") || "Cập nhật thất bại");
        }
    };

    const handleUploadAvatar = async (file: File) => {
        if (!customer) return;
        const res = await uploadChannelCustomerAvatar(customer.id, file, customer.display_name);
        if (res.status) {
            await fetchCustomer(false);
            message.success(t("roomchat.update_success") || "Cập nhật ảnh đại diện thành công");
        } else {
            message.error(t("roomchat.update_failed") || "Cập nhật ảnh đại diện thất bại");
        }
    };


    // Gỡ spam ở cấp khách hàng tổng: xóa sạch logs + cập nhật trạng thái, sau đó nạp lại chi tiết
    const handleUnmarkSpam = async () => {
        if (!customer) return;
        setUnmarkingSpam(true);
        const res = await markSpamCustomer(customer.id, { is_spam: 0 });
        setUnmarkingSpam(false);
        if (res.status) {
            message.success(t("cd.spam_unmark_success") || "Đã gỡ spam khách hàng");
            setOpenSpamModal(false);
            await fetchCustomer(false);
        } else {
            message.error(res.msg || res.message || t("cd.spam_unmark_error") || "Gỡ spam thất bại");
        }
    };

    // Danh sách lịch sử spam (mới nhất lên đầu)
    const spamLogsSorted = [...(customer?.spam_logs || [])].sort(
        (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
    );

    // Spam
    const latestSpam = customer?.spam_logs?.length
        ? customer.spam_logs.reduce((latest, current) => {
            return new Date(current.created_at) > new Date(latest.created_at)
                ? current
                : latest;
        })
        : null;

    return (
        <Layout>
            <Helmet>
                <title>{t('page.customer_details')} | Khothi</title>
            </Helmet>
            <div className="space-y-4">
                <TopHeaderBlock
                    customer={customer}
                    staffOptions={staffOptions}
                    telesaleOptions={telesaleOptions}
                    canEditAssignment={canEditAssignment}
                    onUpdateField={handleUpdateField}
                    onUploadAvatar={handleUploadAvatar}
                />
                {customer?.is_spam === 1 && (
                    <div
                        className="
                                w-full
                                rounded-2xl
                                border border-[#fecaca]
                                bg-[#fff7f7]
                                p-5
                            "
                    >
                        <div className="flex gap-4">
                            <div
                                className="
                                        flex h-10 w-10 shrink-0 items-center justify-center
                                        rounded-full bg-[#fee2e2]
                                    "
                            >
                                <ShieldAlert className="h-5 w-5 text-[#dc2626]" />
                            </div>

                            <div className="flex-1">
                                <div className="mb-2 text-[16px] font-bold text-[#b91c1c]">
                                    Khách hàng bị đánh dấu Spam
                                </div>

                                <div className="flex flex-wrap gap-x-8 gap-y-2 text-[14px]">
                                    <div className="text-[#b91c1c]">
                                        <span className="font-semibold">
                                            Nguyên nhân:
                                        </span>{" "}
                                        {latestSpam?.note}
                                    </div>

                                    <div className="text-[#b91c1c]">
                                        <span className="font-semibold">
                                            Người báo cáo:
                                        </span>{" "}
                                        {latestSpam?.creator?.fullname}
                                    </div>
                                </div>

                                <div className="mt-5 flex flex-wrap items-center gap-3">
                                    <Button
                                        danger
                                        ghost
                                        className="h-[32px] rounded-[6px] px-3 font-semibold !bg-[#ffffff]"
                                        onClick={() => setOpenSpamModal(true)}
                                    >
                                        <div className="flex items-center gap-2 text-[#dc2626]">
                                            <ImageIcon className="h-4 w-4" />
                                            <span className="text-[14px]">Xem lịch sử spam</span>
                                        </div>
                                    </Button>

                                    <Popconfirm
                                        title="Gỡ spam khách hàng"
                                        description="Gỡ spam sẽ xóa sạch toàn bộ lịch sử spam của khách này. Bạn chắc chắn?"
                                        okText="Gỡ spam"
                                        cancelText="Hủy"
                                        okButtonProps={{ danger: true, loading: unmarkingSpam }}
                                        onConfirm={handleUnmarkSpam}
                                    >
                                        <Button
                                            type="primary"
                                            danger
                                            loading={unmarkingSpam}
                                            className="h-[32px] rounded-[6px] px-3 font-semibold"
                                        >
                                            Gỡ spam
                                        </Button>
                                    </Popconfirm>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <Modal
                    open={openSpamModal}
                    footer={null}
                    onCancel={() => setOpenSpamModal(false)}
                    width={720}
                    title={
                        <div className="flex items-center gap-2 text-[#dc2626] text-[18px] font-bold">
                            <ShieldAlert className="h-5 w-5" />
                            Lịch sử spam ({spamLogsSorted.length})
                        </div>
                    }
                    className="
                            dark:[&_.ant-modal-content]:bg-[#1f1f1f]
                            dark:[&_.ant-modal-header]:bg-[#1f1f1f]
                            dark:[&_.ant-modal-title]:text-white
                        "
                >
                    {/* Nút gỡ spam trong modal */}
                    {customer?.is_spam === 1 && (
                        <div className="flex justify-end mb-3">
                            <Popconfirm
                                title="Gỡ spam khách hàng"
                                description="Gỡ spam sẽ xóa sạch toàn bộ lịch sử spam của khách này. Bạn chắc chắn?"
                                okText="Gỡ spam"
                                cancelText="Hủy"
                                okButtonProps={{ danger: true, loading: unmarkingSpam }}
                                onConfirm={handleUnmarkSpam}
                            >
                                <Button danger type="primary" loading={unmarkingSpam}>
                                    Gỡ spam
                                </Button>
                            </Popconfirm>
                        </div>
                    )}

                    {spamLogsSorted.length === 0 ? (
                        <Empty description="Chưa có ghi nhận spam" />
                    ) : (
                        <div className="max-h-[65vh] overflow-y-auto space-y-3">
                            {spamLogsSorted.map((log) => {
                                const images = [log.image_1, log.image_2, log.image_3].filter(Boolean) as string[];
                                return (
                                    <div
                                        key={log.id}
                                        className="rounded-xl border border-[#fee2e2] bg-[#fff7f7] dark:bg-[#2a1f1f] dark:border-[#4b2a2a] p-4"
                                    >
                                        <div className="mb-1 flex items-center justify-between text-[13px]">
                                            <span className="font-semibold text-slate-700 dark:text-slate-200">
                                                {log.creator?.fullname || "—"}
                                            </span>
                                            <span className="text-slate-400">
                                                {log.created_at ? new Date(log.created_at).toLocaleString("vi-VN") : ""}
                                            </span>
                                        </div>
                                        <div className="mb-3 whitespace-pre-line text-[14px] text-slate-700 dark:text-slate-200">
                                            {log.note || "—"}
                                        </div>
                                        {images.length > 0 && (
                                            <Image.PreviewGroup>
                                                <div className="flex flex-wrap gap-2">
                                                    {images.map((src, i) => (
                                                        <Image
                                                            key={i}
                                                            src={src}
                                                            width={110}
                                                            height={110}
                                                            className="rounded-lg object-cover"
                                                            style={{ objectFit: "cover" }}
                                                        />
                                                    ))}
                                                </div>
                                            </Image.PreviewGroup>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Modal>

                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24} md={24} lg={24} xl={8} className="sticky-sidebar">
                        <LeftInformationPanel
                            customer={customer}
                            customerTags={customerTags}
                            careStatuses={careStatuses}
                            occupations={occupations}
                            onUpdateField={handleUpdateField}
                            onUpdateAddress={handleUpdateAddress}
                        />
                    </Col>

                    <Col
                        xs={24}
                        sm={24}
                        md={24}
                        lg={24}
                        xl={16}
                    >
                        <Card
                            className="dark:bg-[#141414] dark:border-[#303030]"
                        >
                            <Tabs
                                className="dark:[&_.ant-tabs-tab]:text-gray-400 
                                        dark:[&_.ant-tabs-tab-active]:text-yellow-400
                                        dark:[&_.ant-tabs-ink-bar]:bg-yellow-400"
                                defaultActiveKey="1"
                                activeKey={activeTab}
                                onChange={(key) => setActiveTab(key)}
                                items={[
                                    {
                                        key: "1",
                                        label: "Tổng quan",
                                        children: (
                                            <div className="space-y-4">
                                                <CustomerProfile />
                                            </div>
                                        ),
                                    },
                                    {
                                        key: "2",
                                        label: "Lịch sử công việc",
                                        children: (
                                            <WorkHistoryPanel customerId={customer?.id} />
                                        ),
                                    },
                                    {
                                        key: "3",
                                        label: "Lịch sử hóa đơn",
                                        children: (
                                            <DemoDashboard />
                                        ),
                                    },
                                    {
                                        key: "4",
                                        label: "Lịch sử cập nhật",
                                        children: (
                                            <HistoryList customerId={customer?.id} />
                                        ),
                                    },
                                ]}
                            />
                        </Card>
                    </Col>
                </Row>
            </div>
        </Layout>
    );
}